import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronDown, Calendar, Menu, X } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  const navigationItems = [
    { href: '/', label: 'Start' },
    { href: '/module', label: 'Module' },
    { href: '/seminartermine', label: 'Seminartermine' },
    { href: '/preise', label: 'Preise Ausbildung' }
  ];

  const moreDropdownItems = [
    { href: '/anmeldung', label: 'Anmeldung' },
    { href: '/behandlungen', label: 'Behandlungen' },
    { href: '/agb', label: 'AGB' },
    { href: '/impressum', label: 'Impressum' },
    { href: '/kooperationen', label: 'Kooperationen' },
    { href: '/lebenswerkstaetten', label: 'Lebenswerkstätten' }
  ];

  const handleDropdownMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setIsDropdownOpen(true);
  };

  const handleDropdownMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 150); // Small delay to prevent flickering
  };

  const handleMenuItemHover = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setIsDropdownOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white/10 backdrop-blur-md'
      }`}
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className={`text-2xl font-bold transition-colors ${
              isScrolled ? 'text-primary-deep' : 'text-white'
            }`}>
              Prana Kundalini Akademie
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navigationItems.map((item) => (
              <Link 
                key={item.href}
                to={item.href}
                className={`px-4 py-2 font-medium transition-all duration-200 hover:scale-105 relative ${
                  location.pathname === item.href 
                    ? `${isScrolled ? 'text-primary-deep' : 'text-white'} after:content-[''] after:absolute after:bottom-0 after:left-4 after:right-4 after:h-0.5 ${isScrolled ? 'after:bg-primary-deep' : 'after:bg-white'}` 
                    : isScrolled 
                      ? 'text-primary-deep hover:text-primary-main' 
                      : 'text-white hover:text-primary-light'
                }`}
                onMouseEnter={handleMenuItemHover}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Custom Mehr Dropdown */}
            <div 
              className="relative" 
              ref={dropdownRef}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleDropdownMouseLeave}
            >
              <button
                className={`flex items-center space-x-1 px-4 py-2 font-medium transition-all duration-200 hover:scale-105 ${
                  isScrolled ? 'text-primary-deep hover:text-primary-main' : 'text-white hover:text-primary-light'
                }`}
              >
                <span>Mehr</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Custom Dropdown Menu */}
              <div 
                className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[200px] z-60 transition-all duration-200 ${
                  isDropdownOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
                }`}
              >
                <div className="py-2">
                  {moreDropdownItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="block w-full px-4 py-2 text-primary-deep hover:text-white hover:bg-primary-main rounded-md transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          {/* Desktop CTA Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button 
              asChild 
              className="bg-primary-light hover:bg-primary-light/80 hover:scale-105 text-primary-deep font-medium border border-primary-light transition-all duration-200"
            >
              <Link to="/anmeldung">
                <Calendar className="mr-2 h-4 w-4" />
                Jetzt Anmelden
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-md transition-colors hover:bg-white/10"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={`h-6 w-6 ${isScrolled ? 'text-primary-deep' : 'text-white'}`} />
            ) : (
              <Menu className={`h-6 w-6 ${isScrolled ? 'text-primary-deep' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 p-4 bg-white rounded-lg shadow-lg border border-gray-200 max-h-[80vh] overflow-y-auto">
            <div className="space-y-4">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`block px-4 py-2 font-medium transition-colors rounded-md relative ${
                    location.pathname === item.href 
                      ? 'text-primary-deep bg-primary-light/20 after:content-[""] after:absolute after:bottom-0 after:left-4 after:right-4 after:h-0.5 after:bg-primary-deep' 
                      : 'text-primary-deep hover:text-white hover:bg-primary-main'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              
              {/* Mobile Mehr Section */}
              <div className="border-t pt-4 border-gray-200">
                <div className="text-sm font-semibold text-primary-deep mb-2">Mehr</div>
                {moreDropdownItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="block px-4 py-2 text-primary-deep hover:text-white hover:bg-primary-main rounded-md transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              {/* Mobile CTA */}
              <Button 
                asChild 
                className="w-full bg-primary-light hover:bg-primary-light/80 hover:scale-105 text-primary-deep font-medium transition-all duration-200"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Link to="/anmeldung">
                  <Calendar className="mr-2 h-4 w-4" />
                  Jetzt Anmelden
                </Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;